import { Link } from 'react-router-dom'
import HeroCTA from '../components/HeroCTA'
import TestimonialSlider from '../components/TestimonialSlider'
import TrustBadges from '../components/TrustBadges'
import StructuredData from '../components/StructuredData'
import './HomePage.css'

function HomePage() {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: 'Laboratory Specialists',
    description: 'Professional laboratory equipment installation services for businesses and universities',
    url: 'https://labspecialists.com',
    telephone: '+1-541-658-9374',
    email: 'info@labspecialists.com',
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'US'
    },
    areaServed: {
      '@type': 'Country',
      name: 'United States'
    },
    serviceType: [
      'Laboratory Equipment Supply',
      'Parts Knowledge & Expertise',
      'Equipment Installation',
      'Service & Support',
      'Consultation & Planning'
    ],
  }

  return (
    <div className="home-page">
      <StructuredData data={structuredData} />
      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <div className="hero-layout">
            {/* LabKeeper Promo */}
            <div className="labkeeper-promo">
              <div className="labkeeper-badge">New Product</div>
              <div className="labkeeper-icon" aria-hidden="true">🔬</div>
              <h2 className="labkeeper-title">Introducing LabKeeper</h2>
              <p className="labkeeper-desc">
                Equipment lifecycle management built for labs. Track assets, schedule maintenance, and extend the life of every instrument.
              </p>
              <a
                href="https://app.labkeeper.org"
                target="_blank"
                rel="noopener noreferrer"
                className="labkeeper-cta"
              >
                Explore LabKeeper →
              </a>
            </div>

            {/* Hero Text */}
            <div className="hero-content">
              <h1 className="hero-title">
                Get the right lab parts—and an install plan you can execute
                <span className="hero-title-accent">without the runaround</span>
              </h1>
              <p className="hero-subtitle">
                Tell us what you're installing or upgrading. We'll map the parts, compatibility, and next steps
                so you can move fast with fewer surprises.
              </p>
              <div className="hero-cta-wrapper">
                <HeroCTA />
              </div>
              <p className="hero-trust-text">
                700+ successful installations • Founded 1988
              </p>
            </div>

            {/* Fume Hood Testing Promo */}
            <div className="fhc-promo">
              <div className="fhc-promo-badge">Featured Service</div>
              <div className="fhc-promo-icon" aria-hidden="true">💨</div>
              <h2 className="fhc-promo-title">Fume Hood Testing & Certification</h2>
              <p className="fhc-promo-desc">
                ASHRAE 110 face velocity testing and annual certification—so you know your hoods actually contain
                fumes, not just look like they do.
              </p>
              <Link to="/fume-hood-certification" className="fhc-promo-cta">
                Learn About Testing →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Three-Image Feature */}
      <section className="home-feature-images" aria-label="Lab installation and innovation">
        <div className="container">
          <div className="feature-images-grid">
            <div className="feature-image-card">
              <img
                src="/assets/Expert_Turnkey_Installation_version_1.png"
                alt="Expert Turnkey Installation — Scaling facilities for pharmaceutical research and university medical excellence."
                className="feature-image"
                loading="lazy"
              />
            </div>
            <div className="feature-image-card">
              <img
                src="/assets/Infrastructure_for_Innovation_version_1.png"
                alt="Infrastructure for Innovation — Precise utility integration ensures your research facility operates at peak efficiency."
                className="feature-image"
                loading="lazy"
              />
            </div>
            <div className="feature-image-card">
              <img
                src="/assets/Saving_Lives_Starts_at_the_Bench_version_1.png"
                alt="Saving Lives Starts at the Bench — Behind every medical breakthrough is a foundation of precision."
                className="feature-image"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="trust-section">
        <div className="container">
          <TrustBadges />
        </div>
      </section>

      {/* Services Overview */}
      <section className="services-overview">
        <div className="container">
          <h2 className="section-title">What you get when you call</h2>
          <p className="section-subtitle">
            A clear parts path + install next steps—so your lab gets operational with fewer delays.
          </p>
          <div className="services-grid">
            <div className="service-card">
              <div className="service-icon">📦</div>
              <h3>Parts + equipment sourcing</h3>
              <p>
                Non-brand specific guidance to select the right casework, fume hoods, sinks, countertops,
                and safety components for your spec and budget.
              </p>
            </div>
            <div className="service-card">
              <div className="service-icon">🔧</div>
              <h3>Compatibility + spec check</h3>
              <p>
                We catch mismatches early (fit, connections, clearances, utilities) so you avoid rework,
                delays, and surprise change orders.
              </p>
            </div>
            <div className="service-card">
              <div className="service-icon">🛠️</div>
              <h3>Install coordination + execution</h3>
              <p>
                Professional installation and setup so your equipment performs as intended from day one.
              </p>
            </div>
            <div className="service-card">
              <div className="service-icon">💡</div>
              <h3>One point of contact</h3>
              <p>
                Plan, source, and install—without juggling vendors and conflicting answers.
              </p>
            </div>
          </div>
          <div className="services-cta">
            <Link to="/products" className="hero-cta hero-cta-secondary">
              Browse categories (so you know what's possible)
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="testimonials-section">
        <div className="container">
          <h2 className="section-title">What lab leaders say after we unblock the project</h2>
          <TestimonialSlider />
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">Get a parts + install game plan (free)</h2>
            <p className="cta-text">
              On a quick call, we'll clarify what you're installing, identify the parts path, and outline next
              steps—so you can move forward with confidence (even if we're not the right fit).
            </p>
            <HeroCTA />
          </div>
        </div>
      </section>
    </div>
  )
}

export default HomePage
